import { useState, useCallback, useRef, useEffect } from 'react';
import { generateId } from '../utils/dragDropTypes';

export const useTimeline = () => {
  const [layers, setLayers] = useState([
    { id: generateId(), name: 'video_layer_1', type: 'video', clips: [], visible: true, locked: false, volume: 1 },
    { id: generateId(), name: 'audio_layer_1', type: 'audio', clips: [], visible: true, locked: false, volume: 1 },
  ]);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30); // Default 30 seconds
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState('9:16'); // Default 9:16
  const playbackIntervalRef = useRef(null);
  const [selectedClipId, setSelectedClipId] = useState(null);

  const selectClip = useCallback((clipId) => {
    setSelectedClipId(clipId);
  }, []);

  // Auto-calculate duration based on clips
  useEffect(() => {
    let maxEndTime = 0;
    layers.forEach(layer => {
      layer.clips.forEach(clip => {
        const end = clip.startTime + clip.duration;
        if (end > maxEndTime) {
          maxEndTime = end;
        }
      });
    });

    // Minimum duration of 1s (to avoid 0), otherwise match content end
    // Logic: If there IS content, exact match. If NO content, default to 10s.
    const newDuration = maxEndTime > 0 ? maxEndTime : 10;
    setDuration(newDuration);
  }, [layers]);

  // Add a new layer
  const addLayer = useCallback((type = 'video') => {
    const layerCount = layers.filter(l => l.type === type).length;
    const newLayer = {
      id: generateId(),
      name: `${type}_layer_${layerCount + 1}`,
      type,
      clips: [],
      visible: true,
      locked: false,
      muted: false,
      volume: 1,
      zIndex: layers.length,
    };
    setLayers(prev => [...prev, newLayer]);
  }, [layers]);

  // Remove a layer
  const removeLayer = useCallback((layerId) => {
    setLayers(prev => prev.filter(layer => layer.id !== layerId));
  }, []);

  // Toggle layer visibility
  const toggleLayerVisibility = useCallback((layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
    ));
  }, []);

  // Toggle layer lock
  const toggleLayerLock = useCallback((layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, locked: !layer.locked } : layer
    ));
  }, []);

  // Toggle layer mute
  const toggleLayerMute = useCallback((layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, muted: !layer.muted } : layer
    ));
  }, []);

  // Update layer volume
  const updateLayerVolume = useCallback((layerId, volume) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, volume: Math.max(0, Math.min(1, volume)) } : layer
    ));
  }, []);

  // Reorder layers
  const reorderLayers = useCallback((dragIndex, hoverIndex) => {
    setLayers(prev => {
      const newLayers = [...prev];
      const [removed] = newLayers.splice(dragIndex, 1);
      newLayers.splice(hoverIndex, 0, removed);
      // Update zIndex for all layers based on new order
      return newLayers.map((layer, idx) => ({ ...layer, zIndex: idx }));
    });
  }, []);

  // Add clip to layer
  const addClipToLayer = useCallback((layerId, mediaFile, startTime = 0, initialData = null) => {
    const isTextClip = !mediaFile || mediaFile.type === 'text' || mediaFile.type === 'text/plain';

    setLayers(prev => prev.map(layer => {
      if (layer.id !== layerId) return layer;

      // Check for shared transform/style
      const useShared = layer.sharedTransform?.enabled;
      const sharedTransform = layer.sharedTransform;

      // Determine style and transform for the new clip
      // If shared is enabled, use the shared values. Otherwise use defaults/initialData
      const clipStyle = {
        fontSize: useShared && sharedTransform.style?.fontSize ? sharedTransform.style.fontSize : (initialData?.style?.fontSize || 20),
        color: useShared && sharedTransform.style?.color ? sharedTransform.style.color : (initialData?.style?.color || '#ffffff'),
        fontFamily: useShared && sharedTransform.style?.fontFamily ? sharedTransform.style.fontFamily : (initialData?.style?.fontFamily || 'Arial'),
        backgroundColor: useShared && sharedTransform.style?.backgroundColor ? sharedTransform.style.backgroundColor : (initialData?.style?.backgroundColor || 'transparent'),
      };

      const clipTransform = useShared ? {
        x: sharedTransform.x,
        y: sharedTransform.y,
        scale: sharedTransform.scale
      } : {
        x: 0.5,
        y: isTextClip ? 0.85 : 0.5, // Default to bottom for text, center for video
        scale: 1,
      };

      const clipAnimation = useShared ? (sharedTransform.animation || { type: 'none', duration: 1 }) : (initialData?.animation || { type: 'none', duration: 1 });

      const clip = {
        id: generateId(),
        mediaFile: mediaFile || { type: 'text', name: 'Text' }, // Handle text clips
        startTime,
        duration: mediaFile?.duration || 5,
        trimStart: 0,
        trimEnd: mediaFile?.duration || 5,
        // If mediaFile has textContent (from .txt file), use it; otherwise use default for text clips
        text: isTextClip ? (mediaFile?.textContent || initialData?.text || 'New Text') : undefined,
        style: isTextClip ? clipStyle : undefined,
        transform: clipTransform,
        animation: isTextClip ? clipAnimation : undefined,
      };

      return { ...layer, clips: [...layer.clips, clip] };
    }));
  }, []);

  // Remove clip from layer
  const removeClip = useCallback((layerId, clipId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? { ...layer, clips: layer.clips.filter(clip => clip.id !== clipId) }
        : layer
    ));
  }, []);

  // Update clip position
  const updateClipPosition = useCallback((layerId, clipId, newStartTime) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          clips: layer.clips.map(clip =>
            clip.id === clipId ? { ...clip, startTime: Math.max(0, newStartTime) } : clip
          ),
        }
        : layer
    ));
  }, []);

  // Update clip duration
  const updateClipDuration = useCallback((layerId, clipId, newDuration) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          clips: layer.clips.map(clip =>
            clip.id === clipId ? { ...clip, duration: Math.max(0.1, newDuration) } : clip
          ),
        }
        : layer
    ));
  }, []);

  // Update generic clip properties (for trimming etc)
  const updateClip = useCallback((layerId, clipId, changes) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id !== layerId) return layer;

      // Logic for shared transform/style
      const useShared = layer.sharedTransform?.enabled;
      let newSharedTransform = layer.sharedTransform;
      let shouldUpdateAllClips = false;

      // If shared mode is ON, and we are updating transform or style, 
      // we need to update the shared state AND all other clips.
      if (useShared) {
        if (changes.transform) {
          newSharedTransform = { ...newSharedTransform, ...changes.transform };
          shouldUpdateAllClips = true;
        }
        if (changes.style) {
          newSharedTransform = {
            ...newSharedTransform,
            style: { ...newSharedTransform.style, ...changes.style }
          };
          shouldUpdateAllClips = true;
        }
        if (changes.animation) {
          newSharedTransform = {
            ...newSharedTransform,
            animation: { ...newSharedTransform.animation, ...changes.animation }
          };
          shouldUpdateAllClips = true;
        }
      }

      // Helper to merge changes into a clip
      const mergeChanges = (clip, extraChanges = {}) => ({
        ...clip,
        ...changes,
        ...extraChanges
      });

      return {
        ...layer,
        sharedTransform: newSharedTransform,
        clips: layer.clips.map(clip => {
          // If we are updating all clips (shared mode), apply transform/style to everyone
          // BUT: we must preserve the 'text' content and other props of other clips
          if (shouldUpdateAllClips) {
            // Apply the shared changes to everyone
            // Note: 'changes' might contain 'text' if the user typed text. 
            // We usually DON'T want to share 'text' content across all clips, only style/transform.
            // So we explicitly filter what we propogate if it's not the target clip.

            if (clip.id === clipId) {
              // This is the target clip, apply everything (including potentially text changes)
              return mergeChanges(clip);
            } else {
              // This is another clip. Apply ONLY the shared transform/style changes.
              const sharedChanges = {};
              if (changes.transform) sharedChanges.transform = changes.transform;
              if (changes.style) sharedChanges.style = { ...clip.style, ...changes.style };
              if (changes.animation) sharedChanges.animation = { ...clip.animation, ...changes.animation };

              return { ...clip, ...sharedChanges };
            }
          }

          // Normal specific update
          return clip.id === clipId ? mergeChanges(clip) : clip;
        }),
      };
    }));
  }, []);

  // Toggle shared transform/style for a layer
  const toggleSharedTransform = useCallback((layerId) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id === layerId) {
        const enabled = !layer.sharedTransform?.enabled;

        let newSharedTransform = layer.sharedTransform || {};
        let newClips = layer.clips;

        if (enabled && layer.clips.length > 0) {
          // Enabling: Capture reference values from the first clip
          const referenceClip = layer.clips[0];

          newSharedTransform = {
            enabled,
            x: referenceClip.transform?.x || 0.5,
            y: referenceClip.transform?.y || 0.85,
            scale: referenceClip.transform?.scale || 1,
            style: referenceClip.style || {
              fontSize: 20,
              color: '#ffffff',
              fontFamily: 'Arial',
              backgroundColor: 'transparent'
            },
            animation: referenceClip.animation || { type: 'none', duration: 1 }
          };

          // Apply these values to ALL clips immediately so they snap to the shared state
          newClips = layer.clips.map(clip => ({
            ...clip,
            transform: {
              x: newSharedTransform.x,
              y: newSharedTransform.y,
              scale: newSharedTransform.scale
            },
            style: newSharedTransform.style ? { ...newSharedTransform.style } : clip.style,
            animation: newSharedTransform.animation ? { ...newSharedTransform.animation } : clip.animation
          }));
        } else {
          // Disabling or just toggling off
          newSharedTransform = { ...newSharedTransform, enabled };
          // We leave clips as they are (they retain the last shared state)
        }

        return {
          ...layer,
          sharedTransform: newSharedTransform,
          clips: newClips
        };
      }
      return layer;
    }));
  }, []);

  // Update layer's shared transform
  const updateLayerTransform = useCallback((layerId, transform) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? {
          ...layer,
          sharedTransform: {
            ...layer.sharedTransform,
            ...transform
          }
        }
        : layer
    ));
  }, []);

  // Playback controls
  const play = useCallback(() => {
    setIsPlaying(true);
    playbackIntervalRef.current = setInterval(() => {
      setCurrentTime(prev => {
        if (prev >= duration) {
          setIsPlaying(false);
          clearInterval(playbackIntervalRef.current);
          return 0;
        }
        return prev + 0.033; // ~30fps
      });
    }, 33);
  }, [duration]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }
  }, []);

  const stop = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }
  }, []);

  const splitClip = useCallback((layerId, clipId, splitTime) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id !== layerId) return layer;

      const clipIndex = layer.clips.findIndex(c => c.id === clipId);
      if (clipIndex === -1) return layer;

      const originalClip = layer.clips[clipIndex];
      const relativeSplitOffset = splitTime - originalClip.startTime;

      // Validate split point is within clip bounds
      if (relativeSplitOffset <= 0 || relativeSplitOffset >= originalClip.duration) {
        return layer;
      }

      // Create two new clips
      const clip1 = {
        ...originalClip,
        id: generateId(),
        duration: relativeSplitOffset,
        trimEnd: (originalClip.trimStart || 0) + relativeSplitOffset
      };

      const clip2 = {
        ...originalClip,
        id: generateId(),
        startTime: splitTime,
        duration: originalClip.duration - relativeSplitOffset,
        trimStart: (originalClip.trimStart || 0) + relativeSplitOffset
      };

      // Replace original with new segments
      const newClips = [...layer.clips];
      newClips.splice(clipIndex, 1, clip1, clip2);

      return { ...layer, clips: newClips };
    }));
  }, []);

  const seek = useCallback((time) => {
    setCurrentTime(Math.max(0, Math.min(time, duration)));
  }, [duration]);

  return {
    layers,
    setLayers,
    currentTime,
    duration,
    isPlaying,
    zoom,
    aspectRatio,
    setZoom,
    setAspectRatio,
    addLayer,
    removeLayer,
    toggleLayerVisibility,
    toggleLayerLock,
    toggleLayerMute,
    reorderLayers,
    addClipToLayer,
    removeClip,
    updateClipPosition,
    updateClipDuration,
    updateClip,
    splitClip,
    toggleSharedTransform,
    updateLayerTransform,
    play,
    pause,
    stop,
    seek,
    updateLayerVolume,
    selectedClipId,
    selectClip,
  };
};
